import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';

interface StudentAnalyticsData {
  totalStudents: number;
  activeStudents: number;
  averageProgress: number;
  completionRate: number;
  topPerformers: Array<{
    id: string;
    name: string;
    position: string;
    branch: string;
    coursesCompleted: number;
    averageScore: number;
  }>;
  engagementStats: Array<{
    period: string;
    activeStudents: number;
    completions: number;
  }>;
  departmentDistribution: Array<{
    department: string;
    studentCount: number;
    percentage: number;
  }>;
  branchStats: Array<{
    branch: string;
    totalEmployees: number;
    trained: number;
    inProgress: number;
    completed: number;
  }>;
}

const StudentAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<StudentAnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const loadAnalytics = async () => {
      setIsLoading(true);
      
      // Simulate API call
      setTimeout(() => {
        setAnalyticsData({
          totalStudents: 2847,
          activeStudents: 1965,
          averageProgress: 73.8,
          completionRate: 82.4,
          topPerformers: [
            {
              id: '1',
              name: 'Trần Minh Đức',
              position: 'Trưởng phòng An ninh',
              branch: 'Nội Bài - Hà Nội',
              coursesCompleted: 12,
              averageScore: 96.8,
            },
            {
              id: '2',
              name: 'Nguyễn Thị Lan Anh',
              position: 'Chuyên viên Dịch vụ Khách hàng',
              branch: 'Tân Sơn Nhất - TP.HCM',
              coursesCompleted: 10,
              averageScore: 94.2,
            },
            {
              id: '3',
              name: 'Lê Hoàng Nam',
              position: 'Kỹ sư Vận hành',
              branch: 'Đà Nẵng',
              coursesCompleted: 9,
              averageScore: 92.5,
            },
            {
              id: '4',
              name: 'Phạm Thị Hồng',
              position: 'Quản lý Chuyến bay',
              branch: 'Cam Ranh - Khánh Hòa',
              coursesCompleted: 8,
              averageScore: 91.7,
            },
            {
              id: '5',
              name: 'Võ Minh Tâm',
              position: 'Chuyên viên Hàng hóa',
              branch: 'Phú Quốc - Kiên Giang',
              coursesCompleted: 8,
              averageScore: 90.3,
            },
          ],
          engagementStats: [
            { period: 'Tuần 1', activeStudents: 1823, completions: 156 },
            { period: 'Tuần 2', activeStudents: 1965, completions: 189 },
            { period: 'Tuần 3', activeStudents: 1756, completions: 142 },
            { period: 'Tuần 4', activeStudents: 1892, completions: 203 },
          ],
          departmentDistribution: [
            { department: 'An ninh Hàng không', studentCount: 856, percentage: 30.1 },
            { department: 'Dịch vụ Khách hàng', studentCount: 654, percentage: 23.0 },
            { department: 'Vận hành Sân bay', studentCount: 512, percentage: 18.0 },
            { department: 'Kỹ thuật & Bảo trì', studentCount: 423, percentage: 14.9 },
            { department: 'Quản lý Chuyến bay', studentCount: 231, percentage: 8.1 },
            { department: 'Hàng hóa & Logistics', studentCount: 171, percentage: 6.0 },
          ],
          branchStats: [
            {
              branch: 'Nội Bài - Hà Nội',
              totalEmployees: 945,
              trained: 723,
              inProgress: 156,
              completed: 567
            },
            {
              branch: 'Tân Sơn Nhất - TP.HCM',
              totalEmployees: 823,
              trained: 634,
              inProgress: 134,
              completed: 500
            },
            {
              branch: 'Đà Nẵng',
              totalEmployees: 467,
              trained: 356,
              inProgress: 89,
              completed: 267
            },
            {
              branch: 'Cam Ranh - Khánh Hòa',
              totalEmployees: 312,
              trained: 245,
              inProgress: 45,
              completed: 200
            },
            {
              branch: 'Phú Quốc - Kiên Giang',
              totalEmployees: 300,
              trained: 234,
              inProgress: 38,
              completed: 196
            }
          ],
        });
        setIsLoading(false);
      }, 1000);
    };

    loadAnalytics();
  }, []);

  if (isLoading) {
    return <div className="w-full py-6 text-center">Đang tải dữ liệu phân tích...</div>;
  }

  if (!analyticsData) {
    return <div className="w-full py-6 text-center">Không có dữ liệu phân tích.</div>;
  }

  // Ensure arrays exist with fallback to empty arrays
  const topPerformers = analyticsData.topPerformers || [];
  const engagementStats = analyticsData.engagementStats || [];
  const departmentDistribution = analyticsData.departmentDistribution || [];
  const branchStats = analyticsData.branchStats || [];

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full px-6 py-6">
        <h1 className="text-2xl font-bold mb-6">Phân tích Nhân viên & Đào tạo</h1>
        <p className="text-gray-600 mb-6">Hệ thống Dịch vụ Sân bay - Thống kê đào tạo nhân sự</p>
        
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 text-blue-800 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Tổng nhân viên</p>
                <p className="text-2xl font-bold text-gray-900">{(analyticsData.totalStudents || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 text-green-800 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Đang đào tạo</p>
                <p className="text-2xl font-bold text-gray-900">{(analyticsData.activeStudents || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 text-purple-800 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Tiến độ TB</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData.averageProgress || 0}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 text-yellow-800 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Tỷ lệ hoàn thành</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData.completionRate || 0}%</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
          {/* Top Performers */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Nhân viên xuất sắc</h2>
            <div className="space-y-4">
              {topPerformers.map((student, index) => (
                <div key={student.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="flex items-center justify-center w-8 h-8 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium mr-3">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="font-medium text-gray-900">{student.name}</h3>
                      <p className="text-sm text-gray-500">{student.position}</p>
                      <p className="text-xs text-blue-600">📍 {student.branch}</p>
                      <p className="text-xs text-gray-400">{student.coursesCompleted} khóa học hoàn thành</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{student.averageScore}%</p>
                    <p className="text-xs text-gray-500">điểm TB</p>
                  </div>
                </div>
              ))}
              {topPerformers.length === 0 && (
                <p className="text-gray-500 text-center py-4">Chưa có dữ liệu nhân viên xuất sắc</p>
              )}
            </div>
          </div>

          {/* Department Distribution */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Phân bố theo Phòng ban</h2>
            <div className="space-y-4">
              {departmentDistribution.map((dept) => (
                <div key={dept.department} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <h3 className="font-medium text-gray-900 w-40 text-sm">{dept.department}</h3>
                    <div className="flex-1 mx-4">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${dept.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{dept.studentCount}</p>
                    <p className="text-xs text-gray-500">{dept.percentage}%</p>
                  </div>
                </div>
              ))}
              {departmentDistribution.length === 0 && (
                <p className="text-gray-500 text-center py-4">Chưa có dữ liệu phòng ban</p>
              )}
            </div>
          </div>
        </div>

        {/* Branch Statistics */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">Thống kê theo Chi nhánh</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {branchStats.map((branch) => (
              <div key={branch.branch} className="text-center p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2 text-sm">{branch.branch}</h3>
                <p className="text-2xl font-bold text-blue-600 mb-1">{branch.totalEmployees}</p>
                <p className="text-xs text-gray-500 mb-3">tổng nhân viên</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-green-600">Đã đào tạo:</span>
                    <span className="font-medium">{branch.trained}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-yellow-600">Đang học:</span>
                    <span className="font-medium">{branch.inProgress}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-blue-600">Hoàn thành:</span>
                    <span className="font-medium">{branch.completed}</span>
                  </div>
                </div>
              </div>
            ))}
            {branchStats.length === 0 && (
              <div className="col-span-full text-center py-4">
                <p className="text-gray-500">Chưa có dữ liệu chi nhánh</p>
              </div>
            )}
          </div>
        </div>

        {/* Engagement Trends */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">Xu hướng Tham gia Hàng tuần</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {engagementStats.map((week) => (
              <div key={week.period} className="text-center p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">{week.period}</h3>
                <p className="text-2xl font-bold text-blue-600 mb-1">{week.activeStudents}</p>
                <p className="text-xs text-gray-500 mb-3">nhân viên tích cực</p>
                <p className="text-lg font-semibold text-green-600">{week.completions}</p>
                <p className="text-xs text-gray-500">hoàn thành khóa học</p>
              </div>
            ))}
            {engagementStats.length === 0 && (
              <div className="col-span-full text-center py-4">
                <p className="text-gray-500">Chưa có dữ liệu xu hướng tham gia</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAnalytics; 